import React, { FC, useState } from "react"
import {
  Box,
  Button,
  Card,
  TextField,
  Typography,
  useTheme,
} from "@mui/material"
import { handlePromise } from "../../../../utils/handlePromisse"
import { changePassword } from "../../../../services/security.service"
import { SimpleSnackbar } from "../../../../components/snackbar"
import { useAuth } from "../../../../contexts/auth2"

interface FieldErrors {
  currentPassword?: string
  newPassword?: string
  confirmPassword?: string
}

const SecurityCard: FC = () => {
  const theme = useTheme()
  const { user } = useAuth()

  const [currentPassword, setCurrentPassword] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [errors, setErrors] = useState<FieldErrors>({})
  const [snackbar, setSnackbar] = useState({ show: false, message: "" })

  const showSnack = (message: string) => setSnackbar({ show: true, message })

  const validate = (): boolean => {
    const next: FieldErrors = {}
    if (!currentPassword) next.currentPassword = "Campo obrigatório"
    if (newPassword.length < 6) next.newPassword = "Mínimo 6 caracteres"
    if (confirmPassword !== newPassword) next.confirmPassword = "Senhas não coincidem"
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) return
    const [, error] = await handlePromise(
      changePassword(user.idUser, { currentPassword, newPassword }),
    )
    if (error) {
      const isUnavailable =
        !error.response || error.response?.status === 404
      showSnack(
        isUnavailable
          ? "Não foi possível atualizar a senha agora."
          : error.response?.data?.message || "Erro ao atualizar senha.",
      )
      return
    }
    showSnack("Senha atualizada com sucesso!")
    setCurrentPassword("")
    setNewPassword("")
    setConfirmPassword("")
    setErrors({})
  }

  const labelSx = {
    "& .MuiInputLabel-root": {
      fontSize: "0.78125rem",
      fontWeight: 600,
      color: "secondary.main",
    },
  }

  return (
    <>
      <SimpleSnackbar
        show={snackbar.show}
        message={snackbar.message}
        onDismiss={() => setSnackbar({ show: false, message: "" })}
      />

      <Card
        variant="outlined"
        sx={{
          borderRadius: "18px",
          p: { xs: "20px 18px", md: "26px 28px" },
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Typography
          sx={{
            fontFamily: theme.typography.fontFamilyDisplay,
            fontWeight: 400,
            fontSize: "1.375rem",
            color: "text.primary",
            mb: 0.5,
          }}
        >
          Segurança
        </Typography>

        <Typography
          sx={{ fontSize: "0.8125rem", color: "text.secondary", mb: 3 }}
        >
          Atualize sua senha de acesso.
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: 2,
          }}
        >
          <TextField
            fullWidth
            label="SENHA ATUAL"
            type="password"
            inputProps={{ "aria-label": "senha atual" }}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            error={!!errors.currentPassword}
            helperText={errors.currentPassword}
            sx={labelSx}
          />

          <TextField
            fullWidth
            label="NOVA SENHA"
            type="password"
            inputProps={{ "aria-label": "nova senha" }}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            error={!!errors.newPassword}
            helperText={errors.newPassword}
            sx={labelSx}
          />

          <TextField
            fullWidth
            label="CONFIRMAR NOVA SENHA"
            type="password"
            inputProps={{ "aria-label": "confirmar nova senha" }}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
            sx={labelSx}
          />
        </Box>

        <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            sx={{ borderRadius: "8px", textTransform: "none", minWidth: 180 }}
          >
            Atualizar senha
          </Button>
        </Box>
      </Card>
    </>
  )
}

export default SecurityCard
